<script setup lang="ts">
import { computed } from 'vue';
import { mapNameToIconAndLabel, type TransactionTableRow } from '@peanuds/shared';
import { useTruncation } from '@peanuds/composables';

import UiHighlightedText from '../../UiHighlightedText.vue';

const {
  row,
  variant = 'recipient',
  highlight,
} = defineProps<{
  row: TransactionTableRow | { currency?: string; label?: string };
  variant?: 'recipient' | 'account';
  /** Search term to mark inside the name. */
  highlight?: string;
}>();

const recipientInfo = computed(() => {
  if (variant === 'account') return { iconClass: '', isIncoming: false, label: '' };

  const transactionRow = row as TransactionTableRow;
  const direction = transactionRow?.direction?.toString().toLowerCase();
  const isIncoming = direction === 'in';
  const directionIconClass =
    transactionRow?.type === 'OUTGOING_PAYMENT' || direction === 'out'
      ? 'pi pi-arrow-up-right'
      : isIncoming
        ? 'pi pi-arrow-down-left'
        : '';

  return {
    iconClass:
      transactionRow?.cardMerchantName === undefined ? directionIconClass : 'pi pi-credit-card',
    isIncoming,
    label: mapNameToIconAndLabel(transactionRow).label,
  };
});

const accountTagLabel = computed(() => {
  if (variant !== 'account') return '';
  const r = row as { currency?: string; label?: string };
  const ccy = r.currency ?? '';
  const label = r.label ?? '';
  return ccy ? `${ccy} ${label}`.trim() : label;
});

const { el: nameLabelEl, isTruncated } = useTruncation();
</script>

<template>
  <span
    :class="{ 'cell-name': true, 'cell-name--truncated': isTruncated }"
    v-tooltip.top="
      isTruncated
        ? {
            value: variant === 'account' ? accountTagLabel : recipientInfo.label,
            showDelay: 300,
            hideDelay: 150,
          }
        : undefined
    "
  >
    <template v-if="variant === 'account'">
      <Tag class="cell-name__account-tag" :value="accountTagLabel" severity="info" />
    </template>

    <template v-else>
      <span
        v-if="recipientInfo.iconClass"
        :class="{
          'cell-name__icon': true,
          'cell-name__icon--incoming': recipientInfo.isIncoming,
        }"
        aria-hidden="true"
      >
        <i :class="recipientInfo.iconClass" />
      </span>
      <span ref="nameLabelEl" class="cell-name__label">
        <UiHighlightedText :text="recipientInfo.label" :query="highlight" />
      </span>
    </template>
  </span>
</template>

<style scoped lang="scss">
.cell-name {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  vertical-align: middle;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;

  &--truncated {
    cursor: pointer;
  }

  &__icon {
    width: 2rem;
    height: 2rem;
    flex-shrink: 0;
    border-radius: 50%;
    background: var(--p-surface-200);
    display: inline-flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 1rem;
      color: var(--p-surface-700);
    }

    &--incoming {
      background: var(--p-tag-success-background);

      i {
        color: var(--p-tag-success-color);
      }
    }
  }

  &__label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  &__account-tag {
    max-width: 100%;
    min-width: 0;

    :deep(.p-tag-value) {
      display: block;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
